import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import React, { useState, useCallback } from "react";
import useFormField from "./useFormField";
import { formatNumber } from "../../utils/UtilFunctions";
import { getUpdatedOptions } from "./formUtils";
import InputNumberField from "./InputNumberField";
import FormCalendarDate from "./FormCalendarDate";

const setLookupFieldEmptyIfOnlySpaceEntered = (setValue, event, name) => {
  if (event.target.value?.length && event.target.value?.trim() === "") {
    event.preventDefault?.();
    event.target.value = "";
    setValue(name, "", { shouldValidate: true });
  }
};

const FormField = ({
  field = {},
  register,
  control,
  errors,
  Controller,
  dropdownOptions = [],
  mode,
  setValue,
  getValues,
  fetchLookupData,
  viewLookupDetails,
  dynamicFieldNature = null,
  openedDropdownsRef,
  clearErrors,
  setError,
  getChangedDateValues = null,
  dropdownStyle = null,
}) => {
  const [previousValidValue, setPreviousValidValue] = useState(0);
  const {
    name,
    label,
    type,
    options = {},
    fieldNature,
    fractionDigits,
    numberMode,
    format = "",
    externalProgram = "",
    readOnlyProgram,
    params = [],
    viewMode = "link",
  } = field;

  const {
    hidden,
    readOnly,
    handleTextChange,
    handleNumberChange,
    handleNumberKeyDown,
    setDate,
    getDate,
    getFieldLabel,
    setDropdownValue,
    getDropdownValue,
    processError,
    navigateToLookup,
  } = useFormField({ setValue, fieldNature, mode, dynamicFieldNature });

  const openLookupModal = useCallback(() => {
    const formData = getValues();
    fetchLookupData(externalProgram, params, formData);
  }, [externalProgram, params]);

  const getComponent = () => {
    switch (type) {
      case "text":
        return (
          <Controller
            name={name}
            control={control}
            rules={getUpdatedOptions(options, readOnly)}
            render={({ field: { name, value } }) => {
              if (readOnly) {
                return <span id={name}>{value}</span>;
              }
              return (
                <>
                  <InputText
                    id={name}
                    name={name}
                    value={value}
                    className={classNames({
                      "p-invalid": errors[name],
                    })}
                    aria-invalid={errors[name]}
                    onChange={(e) =>
                      handleTextChange(e, options.maxLength?.value)
                    }
                    aria-autocomplete="none"
                    autoComplete="off"
                  />
                  {options["required"] && <sup className="star-label">*</sup>}
                </>
              );
            }}
          />
        );
      case "number":
        return (
          <Controller
            name={name}
            control={control}
            rules={options}
            render={({ field: { name, value } }) => {
              if (readOnly) {
                const formattedValue = formatNumber(value, fractionDigits);
                return <span id={name}>{formattedValue}</span>;
              } else {
                return (
                  <>
                    <InputNumberField
                      name={name}
                      value={value}
                      options={options}
                      fractionDigits={fractionDigits}
                      setValue={setValue}
                      numberMode={numberMode}
                      key={name}
                      hasError={!!errors[name]}
                    />
                    {options["required"] && <sup className="star-label">*</sup>}
                  </>
                );
              }
            }}
          />
        );
      case "dropdown":
        return (
          <Controller
            name={name}
            control={control}
            rules={options}
            defaultValue={options[0]?.value}
            render={({ field: { name, value, onChange } }) => {
              if (readOnly) {
                return (
                  <span id={name}>{getFieldLabel(value, dropdownOptions)}</span>
                );
              }
              let hideDropdown = null;
              return (
                <>
                  <Dropdown
                    id={name}
                    inputId={name}
                    value={setDropdownValue(value, dropdownOptions)}
                    onChange={(e) => {
                      const value = getDropdownValue(e.value);
                      onChange(value);
                    }}
                    className={classNames({
                      "p-invalid": errors[name],
                    })}
                    onShow={() => {
                      const openedDropdowns = openedDropdownsRef.current;
                      const openedDropdownsKeys =
                        Object.keys(openedDropdowns) || [];
                      if (openedDropdownsKeys.length) {
                        openedDropdownsKeys.forEach((dropdownKey) => {
                          openedDropdowns[dropdownKey]?.hideDropdown();
                        });
                      }
                      openedDropdownsRef.current[name] = { hideDropdown };
                    }}
                    onHide={() => {
                      delete openedDropdownsRef.current[name];
                    }}
                    ref={(ref) => {
                      if (ref) {
                        hideDropdown = ref.hide;
                      }
                    }}
                    options={dropdownOptions}
                    optionLabel="label"
                    aria-invalid={errors[name]}
                  />
                  {options["required"] && <sup className="star-label">*</sup>}
                </>
              );
            }}
          />
        );
      case "date": {
        const updatedOptions = getUpdatedOptions(options, readOnly, true);
        return (
          <>
            <Controller
              name={name}
              control={control}
              rules={updatedOptions}
              render={({ field: { name, value } }) => {
                if (readOnly) {
                  return <span id={name}>{value}</span>;
                }
                return (
                  <FormCalendarDate
                    {...{
                      name,
                      value,
                      errors,
                      clearErrors,
                      getDate,
                      setDate,
                      getChangedDateValues,
                      options,
                      dropdownStyle,
                      updatedOptions,
                      setError,
                    }}
                  />
                );
              }}
            />
          </>
        );
      }
      case "lookup":
        return (
          <>
            <Controller
              name={name}
              control={control}
              rules={getUpdatedOptions(options, readOnly)}
              render={({ field: { name, value } }) => {
                if (readOnly) {
                  if (viewMode === "text") {
                    return <span id={name}>{value}</span>;
                  }
                  const formData = getValues();
                  return (
                    <Button
                      label={value}
                      link
                      type="button"
                      onClick={() => {
                        viewLookupDetails(
                              externalProgram,
                              params,
                              formData
                            );
                      }}
                    />
                  );
                }
                return (
                  <>
                    {/* <IconField iconPosition="right">
                    <InputIcon className="pi pi-search"> </InputIcon>
                    <InputText
                      name={name}
                      value={value}
                      className={classNames({
                        "p-invalid": errors[name],
                      })}
                      aria-invalid={errors[name]}
                      onChange={(e) =>
                        handleTextChange(e, options.maxLength?.value)
                      }
                    />
                  </IconField> */}
                    <div className="p-inputgroup">
                      <InputText
                        id={name}
                        name={name}
                        value={value}
                        className={classNames({
                          "p-invalid": errors[name],
                        })}
                        aria-invalid={errors[name]}
                        onChange={(e) =>
                          handleTextChange(e, options.maxLength?.value, true)
                        }
                        autoComplete="off"
                        aria-autocomplete="none"
                        onKeyDown={(event) => {
                          const { key } = event;
                          if (key === "F4") {
                            event.preventDefault();
                            openLookupModal();
                          }
                          if (key === 'Enter'){ setLookupFieldEmptyIfOnlySpaceEntered(setValue, event, name);}
                        }}
                        onBlur={(event) => {
                         setLookupFieldEmptyIfOnlySpaceEntered(
                           setValue,
                           event,
                           name
                         );
                        }}
                        tooltip="F4"
                      />
                      <Button
                        id={`lookup-${name}`}
                        icon="pi pi-search"
                        className="lookup-button"
                        type="button"
                        onClick={() => {
                            openLookupModal();
                        }}
                      />
                    </div>
                    {options["required"] && (
                      <sup className="star-label-lookup">*</sup>
                    )}
                  </>
                );
              }}
            />{" "}
          </>
        );
      default:
        return null;
    }
  };

  if (!hidden) {
    return (
      <>
        {label && label.trim() !== "" && (
          <div className={`grid-item label ${format}`}>
            <label htmlFor={name} className="content-input">
              {label}
            </label>
          </div>
        )}
        {type && type.toUpperCase().trim() !== "LABEL" && (
          <div className="grid-item input">
            {getComponent()}
            {errors[name] && (
              <small className="p-error">
                {processError(errors[name].message)}
              </small>
            )}
          </div>
        )}
      </>
    );
  }
  return null;
};

export default FormField;